import { type DetailedHTMLProps, type HTMLAttributes, useEffect, useState } from "react";

import { cva } from "class-variance-authority";

import { CancelIcon } from "@ala/icons/symbols/solid";

import { clsx } from "../../utils/clsx";
import { Button } from "../button/button";
import { Collapsible } from "../collapsible/collapsible";
import type { ToastDetails } from "./toast-context";

const toastVariants = cva("flex items-center gap-4 px-4 py-3 text-sm", {
  variants: {
    color: {
      malachite: "bg-malachite-500 text-contrast-900",
      red: "bg-red-600 text-white",
      yellow: "bg-yellow-500 text-contrast-900",
      blue: "bg-blue-50 text-blue-600",
    } as Record<ToastDetails["color"], string>,
  },
  defaultVariants: {
    color: "malachite",
  },
});

interface ToastProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  details: ToastDetails;
  stage: "opening" | "closing";
  onClose?: () => void;
}

export const Toast = ({ details, onClose, stage, ...props }: ToastProps) => {
  const { color, children, icon } = details;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(stage === "opening");
  }, [stage]);

  return (
    <Collapsible hidden={!visible}>
      <div className="pointer-events-auto w-screen max-w-[600px] px-2 pb-2">
        <div className={clsx(toastVariants({ color }))} {...props}>
          {icon && <span className="shrink-0 text-base">{icon}</span>}
          <p className="grow">{children}</p>

          <Button
            variant="ghost"
            className={clsx("-my-1 -mr-2 self-start p-2", color === "red" && "text-white")}
            onClick={(e) => {
              onClose?.();
              e.preventDefault();
            }}
          >
            <CancelIcon />
          </Button>
        </div>
      </div>
    </Collapsible>
  );
};
